import { useEffect, useMemo, useState } from "react";
import client from "../api/client";

const styles = {
  card: {
    border: 0,
    borderRadius: 14,
    boxShadow: "0 2px 10px rgba(0,0,0,.08)",
    overflow: "hidden",
    background: "#fff",
  },
  header: {
    background: "linear-gradient(90deg,#0b4f4a,#0f766e)",
    color: "#fff",
    padding: "9px 12px",
  },
  title: {
    fontSize: 13,
    fontWeight: 700,
    margin: 0,
  },
  sub: {
    fontSize: 10,
    opacity: 0.85,
  },
  body: {
    padding: 12,
    fontSize: 11,
  },
  label: {
    fontSize: 10,
    fontWeight: 700,
    marginBottom: 3,
    color: "#495057",
  },
  input: {
    height: 30,
    fontSize: 11,
    borderRadius: 7,
  },
  btn: {
    height: 30,
    fontSize: 11,
    borderRadius: 7,
    fontWeight: 600,
  },
  alert: {
    fontSize: 11,
    padding: "7px 10px",
    marginBottom: 8,
  },
  moduleCard: {
    border: "1px solid #e9ecef",
    borderRadius: 12,
    padding: 10,
    height: "100%",
    background: "#f8f9fa",
    boxShadow: "0 1px 4px rgba(0,0,0,.04)",
  },
  moduleTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: "#0d6efd",
    marginBottom: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  permissionLabel: {
    fontSize: 10.5,
    color: "#343a40",
    display: "flex",
    alignItems: "center",
    gap: 6,
    marginBottom: 6,
    cursor: "pointer",
  },
  check: {
    width: 13,
    height: 13,
    cursor: "pointer",
  },
  countBadge: {
    fontSize: 9,
    padding: "3px 6px",
    borderRadius: 20,
  },
};

export default function PermissionsPage() {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [selectedRole, setSelectedRole] = useState("");
  const [selected, setSelected] = useState([]);
  const [roleName, setRoleName] = useState("");
  const [message, setMessage] = useState("");

  const load = async () => {
    const [r, p] = await Promise.all([
      client.get("/security/roles"),
      client.get("/security/permissions"),
    ]);

    setRoles(r.data || []);
    setPermissions(p.data || []);

    if ((r.data || []).length && !selectedRole) {
      setSelectedRole(String(r.data[0].id || r.data[0].Id));
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!selectedRole) return;

    client
      .get(`/security/roles/${selectedRole}/permissions`)
      .then((res) =>
        setSelected(
          (res.data || []).map((x) => x.permissionCode || x.PermissionCode),
        ),
      );
  }, [selectedRole]);

  const grouped = useMemo(
    () =>
      permissions.reduce((a, p) => {
        const m = p.moduleName || p.ModuleName || "General";
        (a[m] = a[m] || []).push(p);
        return a;
      }, {}),
    [permissions],
  );

  const toggle = (code) =>
    setSelected((prev) =>
      prev.includes(code) ? prev.filter((x) => x !== code) : [...prev, code],
    );

  const toggleModule = (list) => {
    const codes = list.map((p) => p.permissionCode || p.PermissionCode);
    const allSelected = codes.every((c) => selected.includes(c));

    setSelected((prev) =>
      allSelected
        ? prev.filter((x) => !codes.includes(x))
        : [...new Set([...prev, ...codes])],
    );
  };

  const save = async () => {
    await client.post("/security/roles/permissions", {
      roleId: Number(selectedRole),
      permissionCodes: selected,
    });

    setMessage("Permissions updated successfully");
  };

  const addRole = async () => {
    if (!roleName.trim()) return;

    await client.post("/security/roles", {
      roleName: roleName.trim(),
    });

    setRoleName("");
    await load();
  };

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <h6 style={styles.title}>
          <i className="bi bi-shield-lock me-2"></i>
          User Roles & Permission Setup
        </h6>
        <div style={styles.sub}>
          Create roles and control module-wise access permissions
        </div>
      </div>

      <div style={styles.body}>
        {message && (
          <div className="alert alert-success" style={styles.alert}>
            <i className="bi bi-check-circle me-1"></i>
            {message}
          </div>
        )}

        <div className="row g-2 mb-2">
          <div className="col-md-4">
            <label style={styles.label}>Select Role</label>
            <select
              className="form-select form-select-sm"
              style={styles.input}
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
            >
              {roles.map((r) => (
                <option key={r.id || r.Id} value={r.id || r.Id}>
                  {r.roleName || r.RoleName}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-4">
            <label style={styles.label}>Create New Role</label>
            <div className="input-group input-group-sm">
              <input
                className="form-control"
                style={styles.input}
                value={roleName}
                onChange={(e) => setRoleName(e.target.value)}
                placeholder="e.g. Accountant"
              />

              <button
                type="button"
                className="btn btn-outline-primary"
                style={styles.btn}
                onClick={addRole}
              >
                <i className="bi bi-plus-circle me-1"></i>
                Add
              </button>
            </div>
          </div>

          <div className="col-md-2 d-flex align-items-end">
            <button
              className="btn btn-primary btn-sm w-100"
              style={styles.btn}
              onClick={save}
              disabled={!selectedRole}
            >
              <i className="bi bi-save me-1"></i>
              Save
            </button>
          </div>

          <div className="col-md-2 d-flex align-items-end">
            <div
              className="w-100 text-center"
              style={{
                height: 30,
                borderRadius: 7,
                background: "#f8f9fa",
                border: "1px solid #dee2e6",
                fontSize: 10,
                paddingTop: 6,
                color: "#495057",
                fontWeight: 600,
              }}
            >
              Selected: {selected.length}
            </div>
          </div>
        </div>

        <div style={{ height: 1, background: "#e9ecef", margin: "10px 0" }} />

        <div className="row g-2">
          {Object.entries(grouped).map(([module, list]) => {
            const codes = list.map((p) => p.permissionCode || p.PermissionCode);
            const selectedCount = codes.filter((c) =>
              selected.includes(c),
            ).length;

            return (
              <div className="col-md-4 col-xl-3" key={module}>
                <div style={styles.moduleCard}>
                  <div style={styles.moduleTitle}>
                    <span>
                      <i className="bi bi-folder-check me-1"></i>
                      {module}
                    </span>

                    <span
                      className="badge text-bg-primary"
                      style={styles.countBadge}
                    >
                      {selectedCount}/{list.length}
                    </span>
                  </div>

                  <button
                    type="button"
                    className="btn btn-light btn-sm w-100 mb-2"
                    style={{
                      ...styles.btn,
                      height: 27,
                      background: "#fff",
                      border: "1px solid #dee2e6",
                    }}
                    onClick={() => toggleModule(list)}
                  >
                    {selectedCount === list.length
                      ? "Unselect All"
                      : "Select All"}
                  </button>

                  {list.map((p) => {
                    const code = p.permissionCode || p.PermissionCode;
                    const checked = selected.includes(code);

                    return (
                      <label key={code} style={styles.permissionLabel}>
                        <input
                          type="checkbox"
                          className="form-check-input mt-0"
                          style={styles.check}
                          checked={checked}
                          onChange={() => toggle(code)}
                        />

                        <span
                          style={{
                            fontWeight: checked ? 600 : 400,
                            color: checked ? "#0d6efd" : "#343a40",
                          }}
                        >
                          {p.permissionName || p.PermissionName}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
